import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Link as RouterLink } from 'react-router-dom'
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material'

import { mutations } from '../../graphql'

/**
 * Shown after a fund-claim success page when the same fund has other advisors with
 * a still-pending invite. `useDirectMutations` picks how each row resolves:
 * - true: donor isn't authenticated yet (right after claimFundNewUser) - accept/decline
 *   via the public, token-secured mutations directly on this page.
 * - false: donor is already logged in (claimFundExistingUser) - link to /claim-fund/:token,
 *   which routes them straight to the already-authenticated approve/decline page.
 */
export function OtherAdvisorInvitesPrompt({ invites, useDirectMutations }) {
  if (!invites || invites.length === 0) return null

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {invites.length > 1 ? 'Other Advisors Requesting Access' : 'Another Advisor Is Requesting Access'}
      </Typography>
      <Stack spacing={2} sx={{ mt: 2 }}>
        {invites.map((invite) =>
          useDirectMutations ? (
            <DirectInviteRow key={invite.token} invite={invite} />
          ) : (
            <LinkInviteRow key={invite.token} invite={invite} />
          )
        )}
      </Stack>
    </Paper>
  )
}

function LinkInviteRow({ invite }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 2,
        bgcolor: 'grey.100',
        borderRadius: 1,
      }}
    >
      <Typography variant="body2">{invite.advisorName} has also requested access to your Giving Fund.</Typography>
      <Button component={RouterLink} to={`/claim-fund/${invite.token}`} variant="outlined" size="small">
        Review
      </Button>
    </Box>
  )
}

function DirectInviteRow({ invite }) {
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)

  const [accept, { loading: accepting }] = useMutation(mutations.fundClaims.acceptSecondaryFundClaimInvite, {
    onCompleted: () => setStatus('accepted'),
    onError: (err) => setError(err.message),
  })
  const [decline, { loading: declining }] = useMutation(mutations.fundClaims.declineFundClaimInvite, {
    onCompleted: () => setStatus('declined'),
    onError: (err) => setError(err.message),
  })

  if (status === 'accepted') {
    return <Alert severity="success">{invite.advisorName} has been connected to your Giving Fund.</Alert>
  }
  if (status === 'declined') {
    return <Alert severity="info">You declined {invite.advisorName}&apos;s request.</Alert>
  }

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {invite.advisorName} has also requested access to your Giving Fund.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          disabled={accepting || declining}
          onClick={() => accept({ variables: { token: invite.token } })}
        >
          {accepting ? 'Connecting...' : 'Connect'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={accepting || declining}
          onClick={() => decline({ variables: { token: invite.token } })}
        >
          {declining ? 'Declining...' : 'Decline'}
        </Button>
      </Stack>
    </Box>
  )
}
